import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from '@react-google-maps/api';
import { ref, onValue } from "firebase/database";
import { db } from "../Firebase/firestore";
import "./Tracking.css";
import {
  FiUser,
  FiBriefcase,
  FiPhone,
  FiMapPin,
  FiTruck,
  FiTarget,
  FiClock,
  FiNavigation
} from 'react-icons/fi';

const containerStyle = {
  width: "100%",
  height: "100%",
};

const Tracking = () => {
  const [transporters, setTransporters] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [sharingEnded, setSharingEnded] = useState(false);

  const [viewState, setViewState] = useState({
    longitude: 77.2090,
    latitude: 28.6139,
    zoom: 14,
  });

  const [pulseScale, setPulseScale] = useState(1);
  const growingRef = useRef(true);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    
  });

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        
      );
      const data = await response.json();

      if (data?.display_name) {
        setTracking((prev) => {
          if (!prev) return prev;
          console.log(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);

          return {
            ...prev,
            address: data.display_name,
          };
        });
      }
    } catch (error) {
      console.error("Address fetch error:", error);
      setTracking((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          address: "Unable to fetch address",
        };
      });
    }
  };

  useEffect(() => {
    const ordersRef = ref(db, "transport_orders");

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setTransporters([]);
        return;
      }

      const trackingOrders = [];

      Object.entries(data).forEach(([uid, userOrders]) => {
        Object.entries(userOrders || {}).forEach(([orderId, order]) => {
          const liveItem =
            order?.dispatch?.items?.find(
              (i) => i?.location?.sharing === true
            ) ||
            order?.dispatch?.items?.find(
              (i) => i?.location
            );

          if (!liveItem?.location) return;

          trackingOrders.push({
            id: orderId,
            uid,
            name: liveItem.driverName || "Unknown Driver",
            companyName: order.clientName || "-",
            driverNumber: liveItem.driverNumber || "-",
            truckNumber:
              liveItem.truckNumber ||
              liveItem.vehicleNumber ||
              liveItem.truckNo ||
              order.truckNumber ||
              "-",
            from: order.from || "-",
            to: order.to || "-",
            ...liveItem.location,
            heading: Number(liveItem.location?.heading ?? 0),
          });
        });
      });

      setTransporters(trackingOrders);
      if (!selectedId && trackingOrders.length > 0) {
        const active = trackingOrders.find(t => t.sharing === true) || trackingOrders[0];
        setSelectedId(active.id);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!transporters.length) return;

    const exists = transporters.some(t => t.id === selectedId);

    if (!exists) {
      const active = transporters.find(t => t.sharing === true) || transporters[0];
      setSelectedId(active.id);
    }
  }, [transporters, selectedId]);

  useEffect(() => {
    if (!selectedId) return;

    const selectedTracking = transporters.find(
      (t) => t.id === selectedId
    );

    if (!selectedTracking) {
      setTracking(null);
      return;
    }

    setTracking(selectedTracking);

    if (selectedTracking?.lat && selectedTracking?.lng) {
      fetchAddress(selectedTracking.lat, selectedTracking.lng);

      setViewState((prev) => ({
        ...prev,
        latitude: selectedTracking.lat,
        longitude: selectedTracking.lng,
        zoom: 16,
      }));
    }

    setSharingEnded(selectedTracking.sharing === false);
  }, [selectedId, transporters]);


  // Direction icon for vehicle heading
  const directionIcon = useMemo(() => {
    if (
      typeof window === "undefined" ||
      !window.google ||
      !window.google.maps ||
      !tracking
    ) {
      return null;
    }
    return {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      rotation: tracking.heading || 0,
      anchor: new window.google.maps.Point(0, 2),
      fillColor: tracking.sharing === true ? "#1E88E5" : "#9E9E9E",
      fillOpacity: 1,
      strokeColor: "#fff",
      strokeWeight: 2.5,
      scale: 7.5,
    };
  }, [tracking]);

  // Pulse animation for active sharing
  useEffect(() => {
    let interval;
    if (tracking?.sharing === true) {
      interval = setInterval(() => {
        setPulseScale((prev) => {
          if (growingRef.current) {
            if (prev < 1.45) {
              const next = Math.min(1.45, prev + 0.09);
              if (next === 1.45) growingRef.current = false;
              return next;
            } else {
              growingRef.current = false;
              return prev;
            }
          } else {
            if (prev > 1) {
              const next = Math.max(1, prev - 0.09);
              if (next === 1) growingRef.current = true;
              return next;
            } else {
              growingRef.current = true;
              return prev;
            }
          }
        });
      }, 110);
    } else {
      setPulseScale(1);
      growingRef.current = true;
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tracking?.sharing]);

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  const sortedTransporters = [...transporters].sort((a, b) => {
    if ((a.sharing === true) === (b.sharing === true)) return 0;
    return a.sharing === true ? -1 : 1;
  });

  return (
    <div className="tracking-container">
      <style>{`
@keyframes trackingLivePulse {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,.7); }
  70% { transform: scale(1.25); box-shadow: 0 0 0 10px rgba(34,197,94,0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}
`}</style>
      {!transporters.length ? (
        <div className="loading">Loading location...</div>
      ) : (
        <>
          <div className="tracking-dashboard">
            <div className="tracking-layout">
              {/* LEFT SIDEBAR */}
              <div className="tracking-sidebar">
                <h4 style={{ marginTop: 0 }}>Transporters</h4>

                {sortedTransporters.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    className={`transporter-card ${selectedId === t.id ? "active" : ""}`}
                  >
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'10px'}}>
                      <div>
                        <div style={{fontWeight:600}}>{t.name || 'Unnamed'}</div>
                        <div style={{fontSize:'12px',color:'#64748b'}}>{t.companyName || '-'}</div>
                        <div style={{fontSize:'12px',color:'#64748b'}}>{t.truckNumber || '-'}</div>
                      </div>

                      <span
                        style={{
                          display:'inline-flex',
                          alignItems:'center',
                          gap:'6px',
                          padding:'4px 10px',
                          borderRadius:'999px',
                          fontSize:'11px',
                          fontWeight:700,
                          background:t.sharing ? '#e8f8ee' : '#f1f5f9',
                          color:t.sharing ? '#15803d' : '#64748b'
                        }}
                      >
                        <span
                          style={{
                            width:'8px',
                            height:'8px',
                            borderRadius:'50%',
                            background:t.sharing ? '#22c55e' : '#94a3b8',
                            animation:t.sharing ? 'trackingLivePulse 1.2s infinite' : 'none'
                          }}
                        />
                        {t.sharing ? 'LIVE' : 'OFF'}
                      </span>
                    </div>
                  </div>
                ))}

                {!transporters.length && (
                  <div>No transporters available</div>
                )}
              </div>
              <div style={{display:'flex',flexDirection:'column',flex:1,gap:'16px'}}>
                <div className="tracking-header">
                  <h3 style={{ margin: 0, marginBottom: "8px" }}>
                    📍 Live Tracking
                  </h3>

                  <div
                    style={{
                      fontSize: "14px",
                      color: "#444",
                      lineHeight: 1.6,
                    }}
                  >
                    {tracking?.address && (
                      <div>{tracking.address}</div>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: "12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 14px",
                      borderRadius: "999px",
                      background: sharingEnded
                        ? "#fff1f1"
                        : "#eefbf3",
                      color: sharingEnded
                        ? "#d93025"
                        : "#188038",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: sharingEnded
                          ? "#d93025"
                          : "#188038",
                      }}
                    ></span>

                    {sharingEnded
                      ? "Location sharing ended"
                      : "Live location sharing active"}
                  </div>
                </div>

                <div className="tracking-info-card">
                  {tracking ? (
                    <div style={{
                      width: "100%",
                      padding: "8px 0",
                    }}>
                      <h4 style={{
                        margin: "0 0 18px 0",
                        fontWeight: 700,
                        fontSize: "19px",
                        letterSpacing: "-0.5px"
                      }}>Order Details</h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
                          gap: "14px 24px",
                          width: "100%"
                        }}
                      >
                        {/* Driver */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Driver</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiUser style={{color:'#2563eb'}} /> {tracking.name || "Unknown"}
                          </div>
                        </div>
                        {/* Company */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Company</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiBriefcase style={{color:'#0d9488'}} /> {tracking.companyName || "-"}
                          </div>
                        </div>
                        {/* Driver Number */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Driver Number</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiPhone style={{color:'#ca8a04'}} /> {tracking.driverNumber || "-"}
                          </div>
                        </div>
                        {/* Route */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Route</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiMapPin style={{color:'#7c3aed'}} /> {tracking.from || "-"} <FiNavigation style={{fontSize:'15px',color:'#64748b'}} /> {tracking.to || "-"}
                          </div>
                        </div>
                        {/* Speed */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Speed</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiTruck style={{color:'#e11d48'}} /> {Math.round(tracking.speed || 0)} km/h
                          </div>
                        </div>
                        {/* Accuracy */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Accuracy</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiTarget style={{color:'#059669'}} /> {Math.round(tracking.accuracy || 0)} m
                          </div>
                        </div>
                        {/* Last Updated */}
                        <div>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Last Updated</div>
                          <div style={{display:'flex',alignItems:'center',gap:'8px',fontWeight:500,fontSize:'16px'}}>
                            <FiClock style={{color:'#3b82f6'}} /> {tracking.updatedAt ? new Date(tracking.updatedAt).toLocaleString() : "-"}
                          </div>
                        </div>
                        {/* Current Address */}
                        <div style={{gridColumn:'1 / -1'}}>
                          <div style={{fontSize:'12px',color:'#64748b',marginBottom:'2px'}}>Current Address</div>
                          <div style={{display:'flex',alignItems:'flex-start',gap:'8px',fontWeight:500,fontSize:'16px',wordBreak:'break-word'}}>
                            <FiMapPin style={{color:'#0ea5e9',marginTop:'2px'}} /> {tracking.address || ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: "#888" }}>
                      Not available currently
                    </div>
                  )}
                </div>

                <div className="tracking-map-wrapper">
                  <div className="tracking-map-container">
                    {sharingEnded && (
                      <div className="tracking-ended-banner">
                        Sharing Ended • Showing Last Known Location
                      </div>
                    )}

                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={{ lat: tracking?.lat || 28.6139, lng: tracking?.lng || 77.2090 }}
                      zoom={viewState.zoom}
                      onZoomChanged={() => {
                       
                      }}
                      onCenterChanged={() => {
                        // Could update center if needed
                      }}
                    >
                      {tracking?.lat && tracking?.lng && (
                        <>
                          {/* Glowing pulse markers for active sharing */}
                          {tracking.sharing === true && (
                            <>
                              <Marker
                                position={{ lat: tracking.lat, lng: tracking.lng }}
                                icon={{
                                  path: window.google.maps.SymbolPath.CIRCLE,
                                  scale: 28 * pulseScale,
                                  fillColor: "#1E88E5",
                                  fillOpacity: 0.22,
                                  strokeWeight: 0,
                                }}
                                zIndex={1}
                              />
                              <Marker
                                position={{ lat: tracking.lat, lng: tracking.lng }}
                                icon={{
                                  path: window.google.maps.SymbolPath.CIRCLE,
                                  scale: 16 * pulseScale,
                                  fillColor: "#1E88E5",
                                  fillOpacity: 0.33,
                                  strokeWeight: 0,
                                }}
                                zIndex={2}
                              />
                            </>
                          )}
                          {/* Main marker with direction icon */}
                          <Marker
                            position={{ lat: tracking.lat, lng: tracking.lng }}
                            icon={directionIcon}
                            optimized={false}
                            zIndex={3}
                          />
                          <InfoWindow
                            position={{ lat: tracking.lat, lng: tracking.lng }}
                            options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
                          >
                            <div style={{ minWidth: "180px" }}>
                              <strong>{tracking.name || "Unknown"}</strong>
                              <br />
                              {tracking.companyName || "-"}
                              <br />
                              Truck Number: {tracking.truckNumber || "-"}
                              <br />
                              Driver Number: {tracking.driverNumber || "-"}
                              <br />
                              Speed: {Math.round(tracking.speed || 0)} km/h
                            </div>
                          </InfoWindow>
                        </>
                      )}
                    </GoogleMap>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Tracking;